import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SERVER_API_URL } from '../app.constants';
import { ErrorHandlerUtil } from '../utils/error-handler.util';

/**
 * Triggers file downloads. Two different backend endpoints live under `/binaries` and they need
 * opposite treatment, so this service picks by path:
 *
 * 1. Public CMS binaries (`BinaryResource`, `/binaries/**`) are `permitAll` and stream the file
 * straight back with its Content-Type and Content-Disposition. Those are handed to the browser as a
 * plain navigation: no XHR, so no CORS (the backend registers CORS for `/api/**` only, which is why
 * fetching these used to fail even though the server answered 200), no pointless bearer token on a
 * public resource, no buffering the whole file in JS memory, and the filename comes from the
 * backend's Content-Disposition.
 *
 * 2. The secured file-share (`SecuredDownloadResource`, `/binaries/content/assets/file-share`) is
 * authenticated and generates a short-lived Azure SAS URL. Previously the browser reached it via a
 * native `<a href>` GET that authenticated through the APIM token cookie; that cookie was removed in
 * the PKCE migration, so those links now 401. We can't fetch the file with HttpClient either: the
 * backend 302-redirects to Azure storage, and XHR following that cross-origin redirect is blocked by
 * CORS (Azure sends no Access-Control-Allow-Origin). So we ask the backend (authenticated, token in
 * the Authorization header via the interceptor) to RETURN the SAS URL (`resolve=true`) and then
 * navigate straight to Azure — again a top-level navigation, and the file streams from storage.
 *
 * Not every download starts from a component: content managers also paste file-share links by hand
 * into CMS rich text (news bodies, accordions, labels), which reaches the browser as a plain
 * `<a href>` and bypasses this service entirely. `interceptLinkClick` below takes those over.
 */
@Injectable({
    providedIn: 'root'
})
export class DownloadService {

    // SecuredDownloadResource.SECURED_DOWNLOAD_PATH — the only subtree that requires a token and
    // that understands `resolve=true`. Everything else under /binaries is a public stream.
    private readonly securedFileSharePath = '/binaries/content/assets/file-share/';

    constructor(private http: HttpClient, private errorHandlerUtil: ErrorHandlerUtil) {}

    download(url: string, filename?: string): void {
        const absolute = this.toAbsoluteApiUrl(url);

        // Files the browser renders inline (PDFs, images) open in a new tab so the SPA is not
        // navigated away from. Everything else (zips, installers) is a genuine download and is
        // triggered in the current tab — navigating to it downloads the file without leaving the
        // page, so no odd blank tab is left behind.
        // Prefer the filename for the type check, but only if it actually carries an extension
        // (display names sometimes don't) — otherwise fall back to the URL, which does.
        const typeHint = filename && this.extension(filename) ? filename : url;

        if (!this.isSecuredFileShare(absolute)) {
            // BinaryResource answers with `Content-Disposition: inline`, so navigating the current
            // tab to a viewable file would replace the SPA with the browser's own viewer. A CMS
            // binary announces its type in neither the URL (`/binaries/{assetId}/{handle}`) nor the
            // name (the Hygraph title), so when the type is unknown open a tab rather than risk it:
            // a viewable file renders there and a real download leaves the SPA untouched.
            this.navigate(absolute, !this.extension(typeHint) || this.isInlineViewable(typeHint));
            return;
        }

        const openInNewTab = this.isInlineViewable(typeHint);

        // For the new-tab case, open the tab inside the click gesture to avoid popup blocking;
        // its location is set once the backend returns the resolved SAS URL.
        const targetWindow = openInNewTab ? window.open('', '_blank') : null;

        this.http.get(this.withResolveParam(absolute), { responseType: 'text' }).subscribe({
            next: (fileUrl) => {
                if (targetWindow) {
                    targetWindow.location.href = fileUrl;
                } else {
                    window.location.href = fileUrl;
                }
            },
            error: (err: HttpErrorResponse) => {
                // Close the pre-opened tab so a failed download doesn't leave a blank tab behind.
                if (targetWindow) {
                    targetWindow.close();
                }
                console.error('Download failed for', absolute, err);
                // Surface a user-facing notification (404 -> "not found", 401/403, timeout, 5xx)
                // via the shared handler so the message and styling match the rest of the app.
                this.errorHandlerUtil.handleError(err);
            }
        });
    }

    /**
     * Takes over clicks on secured file-share links that were authored as plain HTML anchors.
     *
     * Rich text from the CMS is rendered with `[innerHTML]`, so a link a content manager typed into
     * a news article is a normal browser navigation: it never reaches this service, the
     * TokenInterceptor never sees it, no bearer token is sent and `SecuredDownloadResource` answers
     * `{"statusCode":401,"message":"Unauthorized"}`. (Before the PKCE migration the APIM token
     * cookie rode along on that navigation, which is why these links used to work.)
     *
     * AppComponent forwards every document click here, so this covers news bodies, accordions,
     * notifications, the footer and any CMS field added later — content managers can keep pasting
     * plain links. Anything that is not a secured file-share link is left completely alone.
     * @param {MouseEvent} event the click, as received at the document root
     */
    interceptLinkClick(event: MouseEvent): void {
        // Modified clicks ("open in new tab", middle click) belong to the browser: preventing the
        // default here would silently do something other than what the user asked for.
        if (event.defaultPrevented || event.button !== 0 ||
            event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }

        const target = event.target as HTMLElement;
        const anchor = target && typeof target.closest === 'function'
            ? target.closest('a[href]') as HTMLAnchorElement
            : null;

        // `anchor.href` is the browser-resolved absolute URL, so relative and protocol-relative
        // hrefs authored in the CMS are matched as well.
        if (!anchor || !this.isSecuredFileShare(anchor.href)) {
            return;
        }

        event.preventDefault();
        this.download(anchor.href);
    }

    private navigate(fileUrl: string, openInNewTab: boolean): void {
        if (openInNewTab) {
            window.open(fileUrl, '_blank', 'noopener');
        } else {
            window.location.href = fileUrl;
        }
    }

    private isSecuredFileShare(absoluteUrl: string): boolean {
        return absoluteUrl.split('?')[0].indexOf(this.securedFileSharePath) !== -1;
    }

    private isInlineViewable(nameOrUrl: string): boolean {
        const inlineExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'txt', 'html', 'htm'];
        return inlineExtensions.indexOf(this.extension(nameOrUrl)) !== -1;
    }

    private extension(nameOrUrl: string): string {
        const clean = nameOrUrl.split('?')[0].split('#')[0];
        const dot = clean.lastIndexOf('.');
        return dot === -1 ? '' : clean.substring(dot + 1).toLowerCase();
    }

    // Resolve relative/stripped paths onto the API host so the request reaches APIM and matches
    // SERVER_API_URL in the TokenInterceptor (which is what attaches the bearer token).
    private toAbsoluteApiUrl(url: string): string {
        if (/^https?:\/\//i.test(url)) {
            return this.rebaseSecuredUrl(url);
        }
        return this.apiBase() + '/' + url.replace(/^\/+/, '');
    }

    /**
     * Hand-authored CMS links carry the production host (https://community4u.unit4.com/...). On any
     * other environment that host does not match SERVER_API_URL, so the TokenInterceptor would skip
     * the bearer token and the secured endpoint would 401 again — this time for a reason the click
     * handler cannot see. Point secured file-share URLs at the environment the app is actually
     * running against; every other absolute URL (public binaries, external links) is left as
     * authored.
     * @param {string} absoluteUrl the URL exactly as it was authored
     * @returns {string} the same URL, rebased onto SERVER_API_URL when it is a secured file-share link
     */
    private rebaseSecuredUrl(absoluteUrl: string): string {
        const pathAndQuery = absoluteUrl.replace(/^https?:\/\/[^/]*/i, '');
        return this.isSecuredFileShare(pathAndQuery) ? this.apiBase() + pathAndQuery : absoluteUrl;
    }

    private apiBase(): string {
        return SERVER_API_URL.replace(/\/+$/, '');
    }

    private withResolveParam(url: string): string {
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'resolve=true';
    }
}
